import type { ReactNode } from "react";

/** Townsy's own line icons for the statistics screens (24 px grid, 1.8 px rounded strokes in the current colour). */
export type StatIconName =
  | "online"
  | "sessions"
  | "visitors"
  | "eye"
  | "panel"
  | "door"
  | "cursor"
  | "coin"
  | "info"
  | "clock"
  | "warning"
  | "table"
  | "calendar"
  | "cash"
  | "store"
  | "home"
  | "receipt";

const PATHS: Record<StatIconName, ReactNode> = {
  online: (
    <>
      <circle cx="12" cy="12" r="2.2" fill="currentColor" stroke="none" />
      <path d="M8.6 15.4a4.8 4.8 0 0 1 0-6.8M15.4 8.6a4.8 4.8 0 0 1 0 6.8M5.7 18.3a8.9 8.9 0 0 1 0-12.6M18.3 5.7a8.9 8.9 0 0 1 0 12.6" />
    </>
  ),
  sessions: (
    <>
      <path d="M13.5 4.5h4a1.5 1.5 0 0 1 1.5 1.5v12a1.5 1.5 0 0 1-1.5 1.5h-4" />
      <path d="M4 12h10.5M11 8.5l3.5 3.5-3.5 3.5" />
    </>
  ),
  visitors: (
    <>
      <circle cx="9" cy="8.5" r="3" />
      <path d="M3.5 19c.8-3 3-4.6 5.5-4.6s4.7 1.6 5.5 4.6" />
      <circle cx="16.6" cy="9.4" r="2.4" />
      <path d="M15.8 14.5c2.2-.1 4 1.2 4.7 3.8" />
    </>
  ),
  eye: (
    <>
      <path d="M2.5 12s3.5-6.3 9.5-6.3 9.5 6.3 9.5 6.3-3.5 6.3-9.5 6.3S2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="2.8" />
    </>
  ),
  panel: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="2.6" />
      <path d="M8 9h8M8 12.5h8M8 16h4.5" />
    </>
  ),
  door: (
    <>
      <path d="M4.5 10 6 5h12l1.5 5" />
      <path d="M5.5 10.5V19h13v-8.5" />
      <path d="M10.2 19v-4.6h3.6V19" />
      <path d="M4.5 10a1.9 1.9 0 0 0 3.75 0 1.9 1.9 0 0 0 3.75 0 1.9 1.9 0 0 0 3.75 0 1.9 1.9 0 0 0 3.75 0" />
    </>
  ),
  cursor: (
    <>
      <path d="M6 4.5 18 11l-5.4 1.6L10 18z" />
      <path d="m13 13 4.5 4.5" />
    </>
  ),
  coin: (
    <>
      <circle cx="12" cy="12" r="8" />
      <path d="m12 7.9 1.2 2.5 2.7.4-2 1.9.5 2.7-2.4-1.3-2.4 1.3.5-2.7-2-1.9 2.7-.4z" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 11v5.5" />
      <circle cx="12" cy="7.8" r="0.6" fill="currentColor" />
    </>
  ),
  clock: (
    <>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </>
  ),
  warning: (
    <>
      <path d="M10.3 4.6 3 17.6A2 2 0 0 0 4.7 20.5h14.6a2 2 0 0 0 1.7-2.9l-7.3-13a2 2 0 0 0-3.4 0z" />
      <path d="M12 9.5v4.5" />
      <circle cx="12" cy="17" r="0.6" fill="currentColor" />
    </>
  ),
  table: (
    <>
      <rect x="3.5" y="5" width="17" height="14" rx="2" />
      <path d="M3.5 10h17M3.5 14.5h17M9.5 5v14" />
    </>
  ),
  calendar: (
    <>
      <rect x="4" y="5.5" width="16" height="14.5" rx="2.2" />
      <path d="M4 10h16M8.5 3.5v4M15.5 3.5v4" />
    </>
  ),
  cash: (
    <>
      <rect x="2.8" y="6.5" width="18.4" height="11" rx="2" />
      <circle cx="12" cy="12" r="2.6" />
      <path d="M6.2 9.5v5M17.8 9.5v5" />
    </>
  ),
  store: (
    <>
      <path d="M4 9.5 5.3 5h13.4L20 9.5" />
      <path d="M4 9.5a2.7 2.7 0 0 0 5.3 0 2.7 2.7 0 0 0 5.4 0 2.7 2.7 0 0 0 5.3 0" />
      <path d="M5.2 12.3V19h13.6v-6.7M10 19v-4h4v4" />
    </>
  ),
  home: (
    <>
      <path d="M3.8 11.2 12 4.5l8.2 6.7" />
      <path d="M6 9.6V19h12V9.6M10.2 19v-4.6h3.6V19" />
    </>
  ),
  receipt: (
    <>
      <path d="M6 3.5h12v17l-2-1.4-2 1.4-2-1.4-2 1.4-2-1.4-2 1.4z" />
      <path d="M9 8.5h6M9 12h6M9 15.5h3.5" />
    </>
  ),
};

/** Decorative: the text next to an icon always names the thing. */
export function StatIcon({ name, size = 20 }: { name: StatIconName; size?: number }) {
  return (
    <svg
      className="stat-icon-svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {PATHS[name]}
    </svg>
  );
}
